import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus, Eye, FileText, Trash2, Printer, Send, Mail, MessageCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TopBar } from "@/components/PageShell";

const AREAS = [
  { value: "", label: "Todas as áreas" },
  { value: "civil", label: "Direito Civil" },
  { value: "tributario", label: "Direito Tributário" },
  { value: "previdenciario", label: "Direito Previdenciário" },
  { value: "penal", label: "Direito Penal" },
  { value: "familia", label: "Direito de Família" },
  { value: "outros", label: "Outros" },
];
const STATUS = [
  { value: "", label: "Todos" },
  { value: "nao_concluido", label: "Não concluído" },
  { value: "concluido", label: "Concluído" },
];
const AREA_LABEL = Object.fromEntries(AREAS.slice(1).map(a => [a.value, a.label]));

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [area, setArea] = useState("");
  const [uf, setUf] = useState("");
  const [cidade, setCidade] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = { q: q || undefined, sort, area: area || undefined, uf: uf || undefined, cidade: cidade || undefined, status: status || undefined };
    const r = await api.get("/clients", { params });
    setItems(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, [sort, area, uf, status]);

  const del = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    await api.delete(`/clients/${id}`); toast.success("Excluído"); load();
  };

  const sendList = async (channel) => {
    setSending(true);
    try {
      const r = await api.post("/clients-list/send", { channel, filters: { area, uf, cidade, status } });
      if (channel === "whatsapp") { window.open(r.data.wa_url, "_blank"); toast.success(`Lista com ${r.data.count} clientes aberta no WhatsApp`); }
      else { toast.success(`Lista enviada para ${r.data.sent_to}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao enviar"); }
    finally { setSending(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="clients-shell">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full font-sans">
      <div className="flex items-end justify-between mb-6 print:hidden">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#C9953D" }}>HERMES · Escritório</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-[1.05] text-[#132238] flex items-center gap-3">
            <Users className="w-8 h-8" style={{ color: "#C9953D" }}/> Clientes
          </h1>
          <p className="text-slate-600 mt-2">{items.length} cadastrado(s)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => window.print()} className="text-sm px-4 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-2" data-testid="btn-print"><Printer className="w-4 h-4"/> Imprimir</button>
          <button onClick={() => sendList("whatsapp")} disabled={sending} className="text-sm px-4 h-10 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-2" data-testid="btn-send-wa"><MessageCircle className="w-4 h-4"/> Enviar por WhatsApp</button>
          <button onClick={() => sendList("email")} disabled={sending} className="text-sm px-4 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-2" data-testid="btn-send-email"><Mail className="w-4 h-4"/> Enviar por E-mail</button>
          <Link to="/reader" className="gradient-primary text-white text-sm font-medium px-5 py-2.5 rounded-xl inline-flex items-center gap-2 hover:-translate-y-0.5 transition" data-testid="btn-new-client">
            <UserPlus className="w-4 h-4" /> Novo (via OCR)
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4 grid grid-cols-1 md:grid-cols-6 gap-3 mb-6 print:hidden">
        <div className="md:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Nome, CPF, RG, telefone, cidade..."
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"
            data-testid="clients-search"/>
        </div>
        <select value={area} onChange={(e) => setArea(e.target.value)} className="h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="filter-area">
          {AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <input placeholder="UF" maxLength={2} value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} className="h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm uppercase" data-testid="filter-uf"/>
        <input placeholder="Cidade" value={cidade} onChange={(e) => setCidade(e.target.value)} onBlur={load} className="h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="filter-cidade"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="filter-status">
          {STATUS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="hidden print:block px-6 pt-6 pb-2">
          <div className="text-xl font-semibold">Lista de Clientes</div>
          <div className="text-xs text-slate-500">{items.length} registro(s) · {new Date().toLocaleString('pt-BR')}</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Nenhum cliente encontrado.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map(c => (
              <li key={c.id} className="p-4 flex flex-wrap items-center gap-4 hover:bg-slate-50 transition" data-testid={`client-row-${c.id}`}>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm text-slate-600 print:hidden">
                  {c.nome_completo?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{c.nome_completo || 'Sem nome'}
                    {c.status === "concluido" && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Concluído</span>}
                    {AREA_LABEL[c.area] && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200">{AREA_LABEL[c.area]}</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.cpf && `CPF ${c.cpf}`} {c.endereco_cidade && `• ${c.endereco_cidade}/${c.endereco_uf}`} {c.telefone && `• ${c.telefone}`} {c.email && `• ${c.email}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 print:hidden">
                  <Link to={`/clients/${c.id}`} data-testid={`btn-view-${c.id}`} className="p-2 rounded-lg hover:bg-white border border-slate-100 text-slate-600" title="Visualizar"><Eye className="w-4 h-4"/></Link>
                  <Link to={`/clients/${c.id}/new-document`} data-testid={`btn-doc-${c.id}`} className="p-2 rounded-lg hover:bg-white border border-slate-100 text-slate-600" title="Gerar documento"><FileText className="w-4 h-4"/></Link>
                  <button onClick={() => del(c.id)} data-testid={`btn-del-${c.id}`} className="p-2 rounded-lg hover:bg-red-50 border border-slate-100 text-red-500" title="Excluir"><Trash2 className="w-4 h-4"/></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </div>
  );
}
